// Big captions in the lower third, held to about three lines, words revealed at
// an estimated speaking pace from the moment Otto's sound starts (design 7.6;
// the booth's pacing). The newest words stay in view while older lines slide up
// under a fade. The full sentence is in the aria-label; the pacing is visual only.
import { useEffect, useMemo, useState } from "react";
import { captionOffsets, captionWords, visibleWordCount } from "./captions";
import { stripDashes } from "../visibleText";

export interface CaptionOverlayProps {
  text: string;
  /** performance.now() when Otto's sound for this turn started; null until then. */
  startedAt: number | null;
  speaking: boolean;
}

export function CaptionOverlay({ text, startedAt, speaking }: CaptionOverlayProps) {
  const words = useMemo(() => captionWords(text), [text]);
  const offsets = useMemo(() => captionOffsets(words), [words]);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    // Not speaking (or the turn already finished): show everything that arrived.
    if (!speaking || startedAt === null) {
      setShown(startedAt === null && speaking ? 0 : words.length);
      return undefined;
    }
    let frame = 0;
    const tick = () => {
      setShown(Math.min(words.length, visibleWordCount(offsets, performance.now() - startedAt)));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [offsets, speaking, startedAt, words.length]);

  const full = stripDashes(text).trim();
  const visible = words.slice(0, shown).join(" ");
  if (!full || !visible) return null;
  return (
    <div className="consult-call-caption">
      <div className="consult-call-caption__window">
        <p className="consult-call-caption__quote" aria-label={full}>
          <span aria-hidden="true">{visible}</span>
        </p>
      </div>
    </div>
  );
}
